import { css } from 'lit';

const styles = css`
  :host {
    display: block;
    width: 100%;
  }

  .container {
    display: flex;
    align-items: flex-start;
    gap: var(--loquix-message-gap, 12px);
    max-width: var(--loquix-message-max-width, 100%);
    padding: 4px 0;
  }

  /* User messages: align to the right */
  :host([role='user']) .container {
    flex-direction: row-reverse;
  }

  /* ── Avatar column ── */

  .avatar-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
  }

  /* ── Message area ── */

  .message-area {
    flex: 1;
    min-width: 0;
  }

  /* ── Bubble group: constrains bubble + bottom-actions to same width ── */

  .bubble-group {
    display: flex;
    flex-direction: column;
    max-width: 80%;
  }

  :host([role='user']) .bubble-group {
    margin-left: auto;
  }

  .bubble {
    border-radius: var(--loquix-message-bubble-radius, 12px);
    padding: var(--loquix-message-bubble-padding, 10px 14px);
    background: var(--loquix-message-assistant-bg, var(--loquix-surface-color, #f8fafc));
    color: var(--loquix-text-color, #1e293b);
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  :host([role='user']) .bubble {
    background: var(--loquix-message-user-bg, var(--loquix-ai-color-subtle, #ede9fe));
  }

  /* Editing state — expand bubble to fill bubble-group width for comfortable editing */
  .bubble--editing {
    width: 100%;
    box-sizing: border-box;
  }

  /* Error state */
  :host([status='error']) .bubble {
    border: 1px solid var(--loquix-message-error-border-color, #ef4444);
  }

  /* Streaming state — subtle pulsing */
  :host([status='streaming']) .bubble {
    animation: loquix-message-pulse 2s ease-in-out infinite;
  }

  @keyframes loquix-message-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.85;
    }
  }

  .header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    font-size: 0.75rem;
    color: var(--loquix-text-muted-color, #64748b);
  }

  .model {
    font-weight: 500;
  }

  .timestamp {
    font-size: 0.6875rem;
    opacity: 0.7;
  }

  .content {
    position: relative;
    line-height: 1.5;
    font-size: var(--loquix-message-font-size, 0.875rem);
    overflow: hidden;
    transition: max-height 300ms var(--loquix-transition-easing, cubic-bezier(0.4, 0, 0.2, 1));
  }

  .content--collapsed {
    overflow: hidden;
  }

  .content-fade {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 48px;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      transparent,
      var(--loquix-message-user-bg, var(--loquix-ai-color-subtle, #ede9fe))
    );
  }

  .show-more {
    display: inline-block;
    margin-top: 4px;
    padding: 0;
    border: none;
    background: none;
    color: var(--loquix-ai-color, #7c3aed);
    font-size: 0.8125rem;
    font-family: inherit;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity var(--loquix-transition-duration, 200ms)
      var(--loquix-transition-easing, cubic-bezier(0.4, 0, 0.2, 1));
  }

  .show-more:hover {
    opacity: 1;
  }

  /* ── Actions toolbar (outside bubble) ── */

  .actions {
    margin-top: 4px;
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--loquix-transition-duration, 200ms)
      var(--loquix-transition-easing, cubic-bezier(0.4, 0, 0.2, 1));
  }

  :host(:hover) .actions,
  :host(:focus-within) .actions {
    opacity: 1;
    pointer-events: auto;
  }

  /* Hide actions when not complete */
  :host(:not([status='complete'])) .actions {
    display: none;
  }

  /* ── Bottom positions: actions inside bubble-group ── */

  :host([actions-position='bottom-start']) .bubble-group .actions,
  :host(:not([actions-position])) .bubble-group .actions {
    align-self: flex-start;
  }

  :host([actions-position='bottom-end']) .bubble-group .actions {
    align-self: flex-end;
  }

  /* ── Inline positions ── */

  /* Inline-opposite: bubble-group becomes a row so actions sit next to bubble */
  .bubble-group--inline {
    flex-direction: row;
    align-items: flex-start;
    gap: 4px;
  }

  .bubble-group--inline > .bubble {
    flex: 1;
    min-width: 0;
  }

  .bubble-group--inline .actions {
    margin-top: 0;
  }

  /* Avatar-side inline: actions stacked under avatar */
  .avatar-column .actions {
    margin-top: 8px;
  }

  /* Error bar */
  .error-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 0.75rem;
  }

  .error-message {
    color: var(--loquix-message-error-border-color, #ef4444);
  }

  .retry-button {
    border: 1px solid var(--loquix-message-error-border-color, #ef4444);
    background: transparent;
    color: var(--loquix-message-error-border-color, #ef4444);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background var(--loquix-transition-duration, 200ms)
      var(--loquix-transition-easing, cubic-bezier(0.4, 0, 0.2, 1));
  }

  .retry-button:hover {
    background: rgba(239, 68, 68, 0.08);
  }
`;

export default styles;
